import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { DateAvailabilityEmptyState } from "@/features/availability/components/DateAvailabilityEmptyState";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import type { DialogDismissReason } from "@/shared/components/ui/FullScreenDialog";
import { DateCalendar } from "@/shared/components/ui/DateCalendar";
import { DateTimePickerLayout } from "@/shared/components/ui/DateTimePickerLayout";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessDate, formatBusinessTime, getBusinessDateKey } from "@/shared/utils/formatters";
import { useBookingAvailability } from "../hooks/useBookingAvailability";
import { bookingPrefillInstant, bookingPrefillSlot, businessDayBounds } from "../utils/bookingTime";
import { calendarDate, calendarDateKey, validDateKey } from "@/shared/utils/calendarDate";

interface DateTimePickerProps {
  durationMinutes: number;
  intialDate: Date | null;
  selectedTime: Date | null;
  setSelectedTime: (date: Date) => void;
  onClose: (reason?: DialogDismissReason) => void;
  isOpen: boolean;
  initialDateKey?: string;
  initialTime?: string;
  initialStartAt?: string;
  before?: number;
  after?: number;
  locationId?: string;
  excludeId?: string;
}

function DateTimeEditor({ durationMinutes, intialDate, initialDateKey, initialTime, initialStartAt, selectedTime, setSelectedTime, onClose, before = 0, after = 0, locationId: requestedLocation, excludeId }: DateTimePickerProps) {
  const { business, locationId } = useCurrentBusiness();
  const zone = business?.timezone ?? "UTC";
  const today = getBusinessDateKey(new Date(), zone);
  const [dateKey, setDateKey] = useState(() => {
    const instant = intialDate ?? bookingPrefillInstant(initialStartAt);
    const initial = instant ? getBusinessDateKey(instant, zone) : validDateKey(initialDateKey ?? null) ? initialDateKey! : today;
    return initial < today ? today : initial;
  });
  const [draft, setDraft] = useState<Date | null>(selectedTime);
  const [requestedTime, setRequestedTime] = useState(initialTime);
  const [requestedInstant, setRequestedInstant] = useState(() => bookingPrefillInstant(initialStartAt));
  const [feedback, setFeedback] = useState("");
  const [applying, setApplying] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const availability = useBookingAvailability({ dateKey, durationMinutes, before, after, locationId: requestedLocation ?? locationId ?? "", excludeId });
  const startTimes = new Map<string, number[]>();
  const slots = availability.slots.map((slot) => {
    const startLabel = formatBusinessTime(slot.startAt, zone);
    const occurrences = startTimes.get(startLabel) ?? [];
    occurrences.push(slot.startAt.getTime());
    startTimes.set(startLabel, occurrences);
    return { ...slot, startLabel, endLabel: formatBusinessTime(slot.endAt, zone), occurrences };
  });
  const validDuration = Number.isFinite(durationMinutes) && durationMinutes > 0;
  const selection = draft ?? requestedInstant ?? bookingPrefillSlot(availability.slots, zone, null, requestedTime)?.startAt;
  const validSelection = selection && availability.slots.some((slot) => slot.startAt.getTime() === selection.getTime());
  const unavailableReason = validDuration && selection && !availability.isLoading && !availability.isFetching && !availability.error
    ? availability.getUnavailableReason(selection, new Date(selection.getTime() + durationMinutes * 60_000))
    : null;
  const apply = async () => {
    if (!selection || applying) return;
    setApplying(true);
    setFeedback("");
    try {
      const fresh = await availability.refresh();
      if (!mounted.current) return;
      const reason = fresh.getUnavailableReason(selection, new Date(selection.getTime() + durationMinutes * 60_000));
      if (reason) throw new Error(reason);
      setSelectedTime(selection);
      onClose();
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Unable to verify this time.");
    } finally {
      setApplying(false);
    }
  };
  return (
    <DateTimePickerLayout
      className="appointment-time-picker"
      disabled={applying}
      onClose={(reason) => { if (!applying || reason === "navigate") onClose(reason); }}
      footer={<>
        {feedback && <p role="alert" className="text-sm text-destructive">{feedback}</p>}
        <Button className="w-full" disabled={!validSelection || !validDuration || availability.isFetching || applying} onClick={() => void apply()}>{applying ? "Checking availability…" : "Apply date & time"}</Button>
      </>}
      calendar={<>
        <DateCalendar className="appointment-calendar" today={calendarDate(today)} disabled={(date) => calendarDateKey(date) < today} selectedDay={calendarDate(dateKey)} onDateSelect={(date) => { setDateKey(calendarDateKey(date)); setDraft(null); setRequestedTime(undefined); setRequestedInstant(null); setFeedback(""); }} />
        {validDuration && <p className="text-sm text-muted-foreground">{durationMinutes} min · {before} min before / {after} min after</p>}
      </>}
    >
      <Section className="flex min-h-0 flex-1 flex-col gap-2 space-y-0 [&>div:first-child]:shrink-0" title={formatBusinessDate(businessDayBounds(dateKey, zone).startAt, zone, { weekday: "long", day: "numeric", month: "long" })}>
        {unavailableReason && <p role="alert" className="text-sm text-destructive">{unavailableReason}</p>}
        <div className="appointment-time-list date-time-picker-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {excludeId && <p className="mb-2 text-xs text-muted-foreground">Your existing time stays reserved until changes are saved. Close without applying to keep it; rescheduling offers other available times.</p>}
          {!validDuration ? <EmptyState message="Choose services with a positive duration first." /> : !availability.enabled ? <EmptyState message="A business and location are required to load availability." /> : availability.isLoading ? <RowSkeleton /> : availability.error ? (
            <ErrorState message={availability.error.message} onRetry={() => { void availability.refresh().catch((error: unknown) => setFeedback(error instanceof Error ? error.message : "Unable to refresh availability.")); }} />
          ) : availability.slots.length === 0 ? <DateAvailabilityEmptyState dateKey={dateKey} locationId={requestedLocation ?? locationId ?? ""} /> : (
            <div role="group" aria-label="Available start times" className="rounded-2xl border border-border bg-card">
              {slots.map((slot) => {
                const selected = selection?.getTime() === slot.startAt.getTime();
                const occurrence = slot.occurrences.length > 1
                  ? ` (occurrence ${slot.occurrences.indexOf(slot.startAt.getTime()) + 1} of ${slot.occurrences.length})` : "";
                const label = `${slot.startLabel} – ${slot.endLabel}${occurrence}`;
                return (
                  <Button key={slot.startAt.toISOString()} variant="ghost" aria-label={label} aria-pressed={selected} className={`flex min-h-14 w-full flex-wrap justify-between gap-x-3 gap-y-1 whitespace-normal rounded-none border-b border-border px-3 py-2 text-left last:border-0 ${selected ? "bg-matcha text-primary" : ""}`} onClick={() => { setDraft(slot.startAt); setFeedback(""); }}>
                    <span className="flex items-center gap-2"><Check aria-hidden="true" className={selected ? "size-4 shrink-0" : "invisible size-4 shrink-0"} />{slot.startLabel}</span>
                    <span className="text-xs font-normal">{label}</span>
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </Section>
    </DateTimePickerLayout>
  );
}

export function DateTimePicker(props: DateTimePickerProps) {
  return props.isOpen ? <DateTimeEditor {...props} /> : null;
}
